import { useFormik } from "formik";
import { CircleNotch, Question } from "phosphor-react";
import { FC, useState } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { searchTicket } from "../../services/invoice.service";
import { InvoiceGeneratorProps } from "../interfaces/invoice-generator-props";
import Button from "../UI/Button";
import ErrorSpan from "../UI/ErrorSpan";
import Flex from "../UI/Flex";
import Input from "../UI/Input";
import validationSchema from "./ticket-search-validation";

interface TicketSearchProps {
  data: InvoiceGeneratorProps;
  onNextStep: (data: any) => void;
}

const TicketSearch: FC<TicketSearchProps> = ({
  data: initialValues,
  onNextStep,
}) => {
  const [isSearching, setIsSearching] = useState(false);
  const MySwal = withReactContent(Swal);

  const submitHandler = async (values: any) => {
    try {
      setIsSearching(true);

      const response = await searchTicket(
        values.rfc,
        values.ticket,
        values.code
      );

      const data = await response.data;

      console.log(data);

      onNextStep({
        ...values,
        saleId: data.id,
        clientId: data.idCliente,
        email: data.correo,
        name: data.nombre,
        fiscalRegime: data.regimenFiscal,
        postalCode: data.cp,
        saleType: data.tipoVenta,

        street: data.calle,
        exteriorNumber: data.noExterior,
        interiorNumber: data.noInterior,
        settlement: data.colonia,
        city: data.ciudad,
        state: data.estado,
        municipality: data.municipio,
      });
      setIsSearching(false);
    } catch (err: any) {
      let message = "No se encontró ningún ticket o éste ya fue facturado";

      if (err.response && err.response.status === 409) {
        message = err.response.data.message;
      }

      await MySwal.fire({
        html: <label>{message}</label>,
        icon: "error",
        confirmButtonColor: "#84cc16",
      });
      setIsSearching(false);
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: submitHandler,
    validationSchema: validationSchema,
    validateOnMount: true,
  });

  const inputClass = (hasError: boolean) => `
    w-full px-4 py-3 rounded-lg border outline-none transition-all duration-200
    placeholder-slate-400 text-slate-700
    ${hasError
      ? "border-red-300 focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-red-50"
      : "border-slate-300 focus:ring-2 focus:ring-lime-200 focus:border-lime-500 bg-white"
    }
  `;

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-6">
      <div className="space-y-5">

        {/* Campo RFC */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">
            RFC
          </label>
          <input
            type="text"
            placeholder="XAXX010101000"
            maxLength={13}
            className={`${inputClass(
              !!(formik.touched.rfc && formik.errors.rfc)
            )} uppercase`}
            {...formik.getFieldProps("rfc")}
          />
          {formik.touched.rfc && formik.errors.rfc && (
            <p className="text-red-500 text-xs mt-1 ml-1">{formik.errors.rfc}</p>
          )}
        </div>

        {/* Grid para Ticket y Code */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Campo Ticket */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">
              No. Ticket
            </label>
            <input
              type="text" // Cambiado a text para permitir maxlength, pero con inputmode numeric
              inputMode="numeric"
              placeholder="Ej. 12345"
              maxLength={10}
              className={inputClass(
                !!(formik.touched.ticket && formik.errors.ticket)
              )}
              {...formik.getFieldProps("ticket")}
            />
            {formik.touched.ticket && formik.errors.ticket && (
              <p className="text-red-500 text-xs mt-1 ml-1">
                {formik.errors.ticket}
              </p>
            )}
          </div>

          {/* Campo Code */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">
              Código Facturación
            </label>
            <input
              type="text"
              placeholder="Ej. A1B2C3"
              maxLength={20}
              className={`${inputClass(
                !!(formik.touched.code && formik.errors.code)
              )} uppercase`}
              {...formik.getFieldProps("code")}
            />
            {formik.touched.code && formik.errors.code && (
              <p className="text-red-500 text-xs mt-1 ml-1">
                {formik.errors.code}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Botón de Submit */}
      <button
        type="submit"
        disabled={isSearching || !formik.isValid}
        className={`
            w-full py-3.5 rounded-lg font-bold text-white shadow-lg transition-all transform flex justify-center items-center
            ${isSearching || !formik.isValid
            ? "bg-[#84cc16] opacity-50 cursor-not-allowed shadow-none"
            : "bg-[#84cc16] hover:bg-[#65a30d] active:scale-95 shadow-lime-500/30"
          }
        `}
      >
        {isSearching ? (
          <CircleNotch size={20} className="animate-spin" />
        ) : (
          <>
            SIGUIENTE
            <span className="ml-2">→</span>
          </>
        )}
      </button>
    </form>
  );
};

export default TicketSearch;
